/**
 * Cache Tier Registry — Single source of truth for caching behavior.
 * Moved from config/performance/cache-tiers.ts by CONFIG CF-5.
 *
 * Strategy: "Never stale" via stale-while-revalidate.
 *
 * - Non-lookup data uses a short staleTime (30s) with refetch on focus/
 *   reconnect. Users see cached data instantly while a background refetch
 *   runs — fast AND current — but the 30s window rate-limits refetches so a
 *   single tab-focus doesn't fire every query on the page at once.
 *
 * - ETags on the backend dedupe the PAYLOAD (304 when unchanged) but do NOT
 *   save server work — the query still runs on every refetch. So the
 *   staleTime window, not the ETag, is what protects the API from bursts.
 *
 * - Lookup tables are the only exception: they never change at runtime, so
 *   they're fetched once and cached for the entire session.
 *
 * ADDING A NEW ENTITY:
 *   - If it's a lookup table, add it to STATIC_ENTITIES, or to
 *     FRESH_LOOKUP_ENTITIES to keep it on the 'fresh' tier. tests/cache.test.ts
 *     fails on a lookup_* route that is in neither list.
 *   - Otherwise, do nothing — it gets the 'fresh' tier automatically
 */
const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
/**
 * The HTTP Cache-Control value per tier. The web reads them through
 * CACHE_TIERS; the API's plugins/etag-cache.ts sends them. The API keeps its
 * own rule for WHICH entities are static (the `lookup_` prefix), not
 * STATIC_ENTITIES below (config-plan.md §10, F2).
 */
export const CACHE_CONTROL = {
    // Cacheable but always revalidated (ETag → cheap 304). Lookups change
    // rarely, but when they DO (admin edit / migration) a stale copy must
    // not persist in the browser disk cache or an upstream/proxy shared
    // cache beyond the client's reach. `max-age=0, must-revalidate` avoids
    // the old `max-age=86400, stale-while-revalidate=3600` that let the
    // property-type list keep showing OLD values even after a hard reload +
    // cache clear.
    static: 'public, max-age=0, must-revalidate',
    fresh: 'private, no-cache',
};
export const CACHE_TIERS = {
    /**
     * STATIC — Lookup tables and enum values.
     * Fetched once per session. Never refetched.
     * These only change via migrations, not at runtime.
     */
    static: {
        staleTime: Infinity,
        gcTime: 24 * HOUR,
        cacheControl: CACHE_CONTROL.static,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    },
    /**
     * FRESH — Everything else. Refetched on navigation/mount, and on
     * window-focus / reconnect once the data is older than staleTime.
     *
     * staleTime: 30s (NOT 0) is a deliberate rate-limit. With the old
     * staleTime:0 + refetchOnWindowFocus:'always', EVERY active query on a
     * page refetched on every tab-focus and every reconnect — a synchronized
     * burst of 15-20 concurrent requests per focus against a single API
     * instance. Each is a full-cost request server-side: the ETag onSend hook
     * still runs the Supabase query, serializes, and hashes the body — the 304
     * only saves the wire payload, NOT the DB/CPU work. Those bursts are what
     * saturated the single instance and dropped requests at the proxy (the
     * "random error, refresh fixes it" symptom).
     *
     * staleTime 30s + refetchOnWindowFocus:true (not 'always') keeps the
     * "fresh when I come back" feel but only refetches queries older than 30s,
     * collapsing the alt-tab / shared-key stampede. Live-updating views
     * (ingestion job status, auto-import) drive their own refetchInterval, so
     * they are unaffected by this.
     *
     * gcTime: 10 minutes keeps inactive data in memory so navigating
     * back to a page still shows instant cached data.
     */
    fresh: {
        staleTime: 30 * 1000,
        gcTime: 10 * MINUTE,
        cacheControl: CACHE_CONTROL.fresh,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
    },
};
// =============================================================================
// STATIC ENTITIES (everything else defaults to 'fresh')
// =============================================================================
/** Entities that are cached forever. Only lookup tables belong here. */
const STATIC_ENTITIES = new Set([
    'lookup_property_types',
    'lookup_property_statuses',
    'lookup_profile_types',
    'lookup_user_relationship_types',
    'lookup_source_types',
    'lookup_job_types',
    'lookup_job_statuses',
    'lookup_parse_statuses',
    'lookup_normalization_statuses',
    'lookup_transaction_types',
    'lookup_transaction_statuses',
    'lookup_party_roles',
    'lookup_assignment_types',
    'lookup_submission_types',
    'lookup_submission_statuses',
    'lookup_review_statuses',
    'lookup_review_decision_types',
    'lookup_comp_types',
    'lookup_comp_search_strategies',
    'lookup_comp_candidate_eligibility_statuses',
    'lookup_comp_member_roles',
    'lookup_report_types',
    'lookup_report_statuses',
    'lookup_conflict_statuses',
    'lookup_freshness_statuses',
    'lookup_gap_types',
    'lookup_severity_types',
    'lookup_building_classes',
    'lookup_property_uses',
    'lookup_change_types',
    'lookup_operation_types',
]);
/**
 * Lookup tables deliberately left on the 'fresh' tier (nothing reads this
 * list; it is the explicit "not static" half of the guard in
 * tests/cache.test.ts). Moving one to STATIC_ENTITIES is a behaviour change:
 * the web would stop refetching it for the whole session.
 */
export const FRESH_LOOKUP_ENTITIES = new Set([
    'lookup_assignment_sides',
    'lookup_business_entities',
    'lookup_comp_permission_levels',
    'lookup_comp_request_statuses',
    'lookup_conditions_of_sale',
    'lookup_construction_purposes',
    'lookup_departments',
    'lookup_designations',
    'lookup_lease_subtypes',
    'lookup_lease_term_kinds',
    'lookup_listing_agreement_statuses',
    'lookup_listing_platforms',
    'lookup_listing_statuses',
    'lookup_markets',
    'lookup_offices',
    'lookup_parking_types',
    'lookup_pipeline_statuses',
    'lookup_property_rights',
    'lookup_release_note_statuses',
    'lookup_rent_structures',
    'lookup_rollback_statuses',
    'lookup_specialties',
    'lookup_unit_kinds',
]);
// =============================================================================
// HELPERS
// =============================================================================
/** Returns true if the entity is a static lookup table. */
export function isStaticEntity(entity) {
    return STATIC_ENTITIES.has(entity);
}
/** Get the cache tier config for an entity. */
export function getTier(entity) {
    return isStaticEntity(entity) ? CACHE_TIERS.static : CACHE_TIERS.fresh;
}
/** Get the tier name for an entity. */
export function getTierName(entity) {
    return isStaticEntity(entity) ? 'static' : 'fresh';
}
/** Get React Query default options for an entity. */
export function getQueryDefaults(entity) {
    const tier = getTier(entity);
    return {
        staleTime: tier.staleTime,
        gcTime: tier.gcTime,
        refetchOnWindowFocus: tier.refetchOnWindowFocus,
        refetchOnReconnect: tier.refetchOnReconnect,
    };
}
/** Get the Cache-Control header value for an entity. */
export function getCacheControl(entity) {
    return getTier(entity).cacheControl;
}
/** Get all static entity names (for registering query defaults). */
export function getStaticEntities() {
    return [...STATIC_ENTITIES];
}
//# sourceMappingURL=cache-tiers.js.map